import React, { useState, useRef, useEffect } from 'react';

// ── Custom Date Picker ────────────────────────────────────────────────────────
const CAL_DAYS   = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const CAL_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export const CustomDatePicker: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const today = new Date();
  const parsed = value ? new Date(value + 'T00:00') : null;
  const [calYear,  setCalYear]  = useState(parsed?.getFullYear()  ?? today.getFullYear());
  const [calMonth, setCalMonth] = useState(parsed?.getMonth()     ?? today.getMonth());

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const daysInMonth = (m: number, y: number) => new Date(y, m + 1, 0).getDate();
  const firstDay    = new Date(calYear, calMonth, 1).getDay();
  const totalDays   = daysInMonth(calMonth, calYear);
  const cells       = [...Array(firstDay).fill(null), ...Array.from({ length: totalDays }, (_, i) => i + 1)];

  const prevMonth = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); };
  const nextMonth = () => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); };

  const pickDay = (day: number) => {
    const d = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onChange(d);
    setOpen(false);
  };

  const display = parsed
    ? parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Pick date';

  return (
    <div ref={wrapRef} className="relative flex-1">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-1.5 px-2.5 py-1 rounded-lg border transition-colors ${
          value ? 'border-accent/30 bg-accent-soft/10' : 'border-ui-border bg-ui-subtle hover:border-ui-border'
        }`}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className={value ? 'text-accent-text shrink-0' : 'text-ui-text3 shrink-0'}>
          <rect x="3" y="4" width="18" height="18" rx="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        <span className={`text-xs font-medium ${value ? 'text-ui-text' : 'text-ui-text3'}`}>{display}</span>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-2 bg-ui-card border border-ui-border rounded-2xl shadow-lg p-4 z-50 w-72">
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={prevMonth}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-ui-text3 hover:bg-ui-subtle hover:text-ui-text transition-colors">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <span className="text-sm font-semibold text-ui-text">{CAL_MONTHS[calMonth]} {calYear}</span>
            <button type="button" onClick={nextMonth}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-ui-text3 hover:bg-ui-subtle hover:text-ui-text transition-colors">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>

          <div className="grid grid-cols-7 mb-1">
            {CAL_DAYS.map(d => (
              <div key={d} className="text-center text-xs font-semibold text-ui-text3 py-1">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-y-0.5">
            {cells.map((day, i) => {
              if (!day) return <div key={i} />;
              const isToday = day === today.getDate() && calMonth === today.getMonth() && calYear === today.getFullYear();
              const isSel   = parsed && day === parsed.getDate() && calMonth === parsed.getMonth() && calYear === parsed.getFullYear();
              return (
                <button key={i} type="button" onClick={() => pickDay(day)}
                  className={`mx-auto flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                    isSel    ? 'bg-accent-fill text-on-accent font-bold'
                    : isToday ? 'border border-accent text-accent-text font-semibold'
                    :           'text-ui-text hover:bg-accent-soft/40'
                  }`}>
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

// ── Custom Time Picker ────────────────────────────────────────────────────────
function parseTime(value: string): { hour: number; minute: number; ampm: 'AM' | 'PM' } {
  if (!value) return { hour: 12, minute: 0, ampm: 'PM' };
  const [h, m] = value.split(':').map(Number);
  return { hour: h % 12 || 12, minute: Math.floor(m / 5) * 5, ampm: h >= 12 ? 'PM' : 'AM' };
}

function toTimeString(hour: number, minute: number, ampm: 'AM' | 'PM'): string {
  const h24 = ampm === 'AM' ? (hour === 12 ? 0 : hour) : (hour === 12 ? 12 : hour + 12);
  return `${String(h24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export const CustomTimePicker: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const { hour, minute, ampm } = parseTime(value);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const set = (h: number, m: number, ap: 'AM' | 'PM') => onChange(toTimeString(h, m, ap));

  const ChevBtn: React.FC<{ dir: 'up' | 'down'; onClick: () => void }> = ({ dir, onClick }) => (
    <button type="button" onClick={onClick}
      className="w-8 h-7 flex items-center justify-center rounded-lg text-ui-text3 hover:bg-ui-subtle hover:text-ui-text transition-colors">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        {dir === 'up' ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
      </svg>
    </button>
  );

  const display = value
    ? new Date(`2000-01-01T${value}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    : 'Pick time';

  return (
    <div ref={wrapRef} className="relative flex-1">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-1.5 px-2.5 py-1 rounded-lg border transition-colors ${
          value ? 'border-accent/30 bg-accent-soft/10' : 'border-ui-border bg-ui-subtle hover:border-ui-border'
        }`}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className={value ? 'text-accent-text shrink-0' : 'text-ui-text3 shrink-0'}>
          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
        <span className={`text-xs font-medium ${value ? 'text-ui-text' : 'text-ui-text3'}`}>{display}</span>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-2 bg-ui-card border border-ui-border rounded-2xl shadow-lg p-4 z-50 w-full min-w-[200px]">
          <div className="flex items-center justify-center gap-3">
            <div className="flex flex-col items-center gap-1">
              <ChevBtn dir="up"   onClick={() => set(hour === 12 ? 1 : hour + 1, minute, ampm)} />
              <span className="text-h2 text-ui-text w-10 text-center tabular-nums">
                {String(hour).padStart(2, '0')}
              </span>
              <ChevBtn dir="down" onClick={() => set(hour === 1 ? 12 : hour - 1, minute, ampm)} />
            </div>

            <span className="text-h2 text-ui-text3 pb-0.5 select-none">:</span>

            <div className="flex flex-col items-center gap-1">
              <ChevBtn dir="up"   onClick={() => set(hour, (minute + 5) % 60, ampm)} />
              <span className="text-h2 text-ui-text w-10 text-center tabular-nums">
                {String(minute).padStart(2, '0')}
              </span>
              <ChevBtn dir="down" onClick={() => set(hour, minute === 0 ? 55 : minute - 5, ampm)} />
            </div>

            <div className="flex flex-col gap-1.5 ml-1">
              {(['AM', 'PM'] as const).map((ap) => (
                <button key={ap} type="button" onClick={() => set(hour, minute, ap)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ${
                    ampm === ap ? 'bg-accent-fill text-on-accent' : 'bg-ui-subtle text-ui-text3 hover:bg-ui-hover hover:text-ui-text'
                  }`}>
                  {ap}
                </button>
              ))}
            </div>
          </div>

          <button type="button" onClick={() => { if (!value) onChange(toTimeString(hour, minute, ampm)); setOpen(false); }}
            className="mt-4 w-full py-2 rounded-xl btn-sheen text-on-primary text-sm font-semibold">
            Done
          </button>
        </div>
      )}
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────
